import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Checkout from "./Checkout";
import PlaceOrder from "./PlaceOrder";
import { getBasketItems, lineItemsTotal } from "@/app/selectors/lineItem";
import { formatMoney } from "@/app/utils/currency";
import { useTranslateText } from "@/app/utils/translations";
import { clearBasket } from "@/app/actions/checkout";
import ErrorModal from "@/app/containers/ErrorModal";
import PlacedOrderModal from "./PlacedOrderModal";
import EditItems from "../EditItems";
import {
  cleanupAsync,
  revertChildLinesAsync,
  deleteBasketAsync,
  addToBasketAsync,
} from "@/app/actions/options";
import { getOptionPriceForBasketLineTotal } from "@/app/selectors/options";
import { getIsOrderProcessing } from "@/app/selectors/order";
import { getAllProductsById } from "@/app/selectors/product";
import { lineItemsSelector } from "@/app/selectors/lineItem";
import ComboModal from "@/app/components/Home/ComboModal";
import { Product } from "@/app/types/product";
import { SelectedComboChoices } from "@/app/types/combo-type";
import { Dispatch } from "redux";
import {
  AddToBasketAsyncAction,
  CleanupAsyncAction,
  DeleteBasketAsyncAction,
  RevertChildLinesAsyncAction,
} from "@/app/types/option";
import { ClearBasketAction } from "@/app/types/line-items";

const Basket = () => {
  const dispatch =
    useDispatch<
      Dispatch<
        | AddToBasketAsyncAction
        | ClearBasketAction
        | CleanupAsyncAction
        | RevertChildLinesAsyncAction
        | DeleteBasketAsyncAction
      >
    >();
  const { locale } = useTranslateText();
  const { priceTotal } = useSelector(lineItemsTotal);
  const lineItems = useSelector(getBasketItems);
  const [lineItemId, setLineItemId] = useState<string>("");
  const [comboItemId, setComboItemId] = useState<string>("");
  const [comboQuantity, setComboQuantity] = useState(1);
  const isBasketEmpty = !lineItems || lineItems.length === 0;
  const optionPriceForBasketLineTotal = useSelector(
    getOptionPriceForBasketLineTotal,
  );
  const isOrderProcessing = useSelector(getIsOrderProcessing);
  const total = priceTotal + optionPriceForBasketLineTotal;

  const onModalClose = () => {
    setLineItemId("");
    dispatch(cleanupAsync(lineItemId));
  };

  const initiateCleanup = () => {
    onModalClose();
    dispatch(revertChildLinesAsync(lineItemId));
  };
  const productsById = useSelector(getAllProductsById);
  const allLineItems = useSelector(lineItemsSelector);

  const handleEditItem = (id: string) => {
    const lineItem = allLineItems.find((item) => String(item.id) === id);
    if (lineItem?.comboSelections) {
      setComboQuantity(lineItem.quantity);
      setComboItemId(id);
    } else {
      setLineItemId(id);
    }
  };

  const comboLineItem = allLineItems.find(
    (item) => String(item.id) === comboItemId,
  );
  const comboProduct = comboLineItem
    ? productsById[comboLineItem.productId]
    : undefined;
  const initialComboSelections = comboLineItem?.comboSelections ?? {};
  const initialComboSelectionsForModal = Object.fromEntries(
    Object.entries(initialComboSelections).map(([sectionId, choices]) => [
      Number(sectionId),
      Object.fromEntries(
        choices.map((choice) => [choice.productId, choice.quantity]),
      ),
    ]),
  );
  const onAddToOrder = (
    product: Product,
    quantity: number,
    comboSelections: Record<number, SelectedComboChoices[]>,
  ) => {
    dispatch(deleteBasketAsync({ lineId: comboItemId }));
    dispatch(addToBasketAsync({ item: product, quantity, comboSelections }));
    setComboItemId("");
  };
  const onRemoveComboItem = () => {
    dispatch(deleteBasketAsync({ lineId: comboItemId }));
    setComboItemId("");
  };

  return (
    <>
      <Checkout
        onResetOrder={() => {
          dispatch(clearBasket());
        }}
        onEditItem={handleEditItem}
      />
      <PlaceOrder
        basketTotal={formatMoney(total, true, locale).amount}
        disabled={isBasketEmpty || isOrderProcessing}
        buttonText="tpos.basketArea.placeOrder"
        onPlaceOrderPress={() => {}}
      />
      <EditItems
        onUpdatePress={initiateCleanup}
        onClose={initiateCleanup}
        lineItemId={lineItemId}
        visible={lineItemId !== ""}
      />
      {comboProduct && (
        <ComboModal
          visible
          product={comboProduct}
          initialSelections={initialComboSelectionsForModal}
          quantity={comboQuantity}
          setQuantity={setComboQuantity}
          onClose={() => setComboItemId("")}
          onAddToOrder={onAddToOrder}
          onRemoveItem={onRemoveComboItem}
        />
      )}
      <PlacedOrderModal />
      <ErrorModal />
    </>
  );
};

export default Basket;
